import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Fades elements marked [data-reveal] in as they enter the viewport.
 *
 * Two things make this trickier than a one-shot IntersectionObserver:
 *
 * 1. Client-side navigation (a language switch, or moving between articles)
 *    reuses the same component and replaces its children. Nodes created after
 *    mount were never observed, so they kept opacity 0 forever — visible as
 *    blank gaps mid-article. The effect re-runs per route, and a
 *    MutationObserver picks up anything added in between.
 * 2. Elements already revealed must stay revealed; only [data-reveal] without
 *    .revealed is (re-)observed.
 */
export function useReveal(rootRef?: React.RefObject<HTMLElement | null>) {
  const { pathname } = useLocation();

  useEffect(() => {
    const root: ParentNode = rootRef?.current ?? document;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      root.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -60px 0px' }
    );

    const observeUnrevealed = () =>
      root.querySelectorAll('[data-reveal]:not(.revealed)').forEach((el) => observer.observe(el));

    observeUnrevealed();

    // Coalesce bursts of DOM changes into one pass per frame.
    let frame = 0;
    const mutationObserver = new MutationObserver(() => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        observeUnrevealed();
      });
    });
    mutationObserver.observe(rootRef?.current ?? document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [rootRef, pathname]);
}
